import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AlgorithmTranslationsAccordion from "./AlgorithmTranslationsAccordion";
import { EditorState, convertFromRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import Div from "@jumbo/shared/Div";
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from "react-sortable-hoc";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  AccordionDetailsStyled,
  AccordionStyled,
  AccordionSummaryStyled,
} from "../../components/mui/Accordions/StyledAccordion/StyleAccordion";
import CustomDragHandle from "components/CustomDragIcon";

let Editor = () => <React.Fragment />;

// Sortable translation item
const SortableTranslationItem = SortableElement(
  ({
    translation,
    tIndex,
    expandedIndex,
    handleItemClick,
    handleDeleteTranslation,
    handleTranslationChange,
    editorStates,
    handleEditorStateChange,
    languages,
    handleAddAlgorithmTranslation,
    handleAlgorithmTranslationChange,
    handleDeleteAlgorithmTranslation,
    handleAddCodeSnippet,
    handleCodeSnippetChange,
    handleDeleteCodeSnippet,
    programmingLanguages,
  }) => (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        mb: 1,
        backgroundColor: expandedIndex === tIndex ? "#f5f5f5" : "inherit",
        position: "relative",
        visibility: "visible !important",
        opacity: "1 !important",
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 1,
          px: 2,
          cursor: "pointer",
        }}
        onClick={() => {
          handleItemClick(tIndex);
        }}
      >
        <CustomDragHandle />
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {translation.langCode || "New Translation"}
        </Typography>
        <IconButton
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteTranslation(tIndex);
          }}
        >
          <DeleteIcon />
        </IconButton>
        <ExpandMoreIcon
          sx={{
            transform:
              expandedIndex === tIndex ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>
      {expandedIndex === tIndex && (
        <Box sx={{ padding: "16px", borderTop: "1px solid #e0e0e0" }}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <FormControl sx={{ mb: 2, minWidth: "200px" }}>
              <Select
                value={translation.langCode || ""}
                onChange={(e) =>
                  handleTranslationChange(tIndex, "langCode", e.target.value)
                }
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Language Code
                </MenuItem>
                {languages.map((lang) => (
                  <MenuItem key={lang.key} value={lang.key}>
                    {lang.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Title Translation"
              value={translation.titleTranslation || ""}
              onChange={(e) =>
                handleTranslationChange(
                  tIndex,
                  "titleTranslation",
                  e.target.value
                )
              }
              sx={{ mb: 2, width: "100%" }}
            />
          </Box>
          <Div sx={{ flex: 1, mb: 2, border: "1px solid lightgray" }}>
            <Editor
              editorStyle={{
                padding: "8px",
                width: "100%",
                minHeight: 100,
                borderWidth: 1,
                borderTopStyle: "solid",
                borderColor: "lightgray",
              }}
              editorState={editorStates[tIndex]}
              onEditorStateChange={(newState) =>
                handleEditorStateChange(tIndex, newState)
              }
            />
          </Div>
          <AlgorithmTranslationsAccordion
            translationIndex={tIndex}
            algorithmTranslations={translation.algorithmTranslations || []}
            handleAddAlgorithmTranslation={handleAddAlgorithmTranslation}
            handleAlgorithmTranslationChange={handleAlgorithmTranslationChange}
            handleDeleteAlgorithmTranslation={handleDeleteAlgorithmTranslation}
            handleAddCodeSnippet={handleAddCodeSnippet}
            handleCodeSnippetChange={handleCodeSnippetChange}
            handleDeleteCodeSnippet={handleDeleteCodeSnippet}
            programmingLanguages={programmingLanguages}
          />
        </Box>
      )}
    </Box>
  )
);

// Sortable container for translations
const SortableTranslationsList = SortableContainer(
  ({
    problemTranslations,
    expandedIndex,
    handleItemClick,
    handleDeleteTranslation,
    handleTranslationChange,
    editorStates,
    handleEditorStateChange,
    languages,
    handleAddAlgorithmTranslation,
    handleAlgorithmTranslationChange,
    handleDeleteAlgorithmTranslation,
    handleAddCodeSnippet,
    handleCodeSnippetChange,
    handleDeleteCodeSnippet,
    programmingLanguages,
  }) => {
    return (
      <AccordionDetailsStyled>
        {problemTranslations.map((translation, tIndex) => (
          <SortableTranslationItem
            key={translation.id || `translation-${tIndex}`}
            index={tIndex}
            translation={translation}
            tIndex={tIndex}
            expandedIndex={expandedIndex}
            handleItemClick={handleItemClick}
            handleDeleteTranslation={handleDeleteTranslation}
            handleTranslationChange={handleTranslationChange}
            editorStates={editorStates}
            handleEditorStateChange={handleEditorStateChange}
            languages={languages}
            handleAddAlgorithmTranslation={handleAddAlgorithmTranslation}
            handleAlgorithmTranslationChange={handleAlgorithmTranslationChange}
            handleDeleteAlgorithmTranslation={handleDeleteAlgorithmTranslation}
            handleAddCodeSnippet={handleAddCodeSnippet}
            handleCodeSnippetChange={handleCodeSnippetChange}
            handleDeleteCodeSnippet={handleDeleteCodeSnippet}
            programmingLanguages={programmingLanguages}
          />
        ))}
      </AccordionDetailsStyled>
    );
  }
);

const TranslationsAccordion = ({
  problemTranslations,
  handleAddTranslation,
  handleTranslationChange,
  handleDeleteTranslation,
  handleAddAlgorithmTranslation,
  handleAlgorithmTranslationChange,
  handleDeleteAlgorithmTranslation,
  handleAddCodeSnippet,
  handleCodeSnippetChange,
  handleDeleteCodeSnippet,
  programmingLanguages,
  languages,
  expanded,
  onChange,
}) => {
  const [editorStates, setEditorStates] = useState(() =>
    problemTranslations.map((translation) => {
      if (
        translation.descriptionTranslation &&
        typeof translation.descriptionTranslation === "string"
      ) {
        if (translation.descriptionTranslation.startsWith('{"blocks":')) {
          try {
            const rawContent = JSON.parse(translation.descriptionTranslation);
            return EditorState.createWithContent(convertFromRaw(rawContent));
          } catch (e) {
            console.error("Failed to parse descriptionTranslation:", e);
            return EditorState.createEmpty();
          }
        } else {
          try {
            const contentState = stateFromHTML(
              translation.descriptionTranslation || "<p></p>"
            );
            return EditorState.createWithContent(contentState);
          } catch (e) {
            console.error(
              "Failed to parse HTML content:",
              e,
              translation.descriptionTranslation
            );
            return EditorState.createEmpty();
          }
        }
      }
      return EditorState.createEmpty();
    })
  );

  const [expandedIndex, setExpandedIndex] = useState(null);

  // Store previous problemTranslations
  const prevTranslationsRef = useRef(problemTranslations);

  useEffect(() => {
    Editor = require("react-draft-wysiwyg").Editor;
  }, []);

  // Update editorStates when problemTranslations changes
  useEffect(() => {
    setEditorStates((prevStates) => {
      const prevTranslations = prevTranslationsRef.current;

      const newEditorStates = problemTranslations.map((translation, index) => {
        const prevTranslation = prevTranslations[index];

        if (
          prevTranslation &&
          prevTranslation.descriptionTranslation ===
            translation.descriptionTranslation &&
          index < prevStates.length
        ) {
          return prevStates[index] || EditorState.createEmpty();
        }

        if (
          translation.descriptionTranslation &&
          typeof translation.descriptionTranslation === "string"
        ) {
          if (translation.descriptionTranslation.startsWith('{"blocks":')) {
            try {
              const rawContent = JSON.parse(translation.descriptionTranslation);
              return EditorState.createWithContent(convertFromRaw(rawContent));
            } catch (e) {
              console.error("Failed to parse descriptionTranslation:", e);
              return EditorState.createEmpty();
            }
          } else {
            try {
              const contentState = stateFromHTML(
                translation.descriptionTranslation || "<p></p>"
              );
              return EditorState.createWithContent(contentState);
            } catch (e) {
              console.error(
                "Failed to parse HTML content:",
                e,
                translation.descriptionTranslation
              );
              return EditorState.createEmpty();
            }
          }
        }
        return EditorState.createEmpty();
      });

      prevTranslationsRef.current = problemTranslations;

      return newEditorStates;
    });
  }, [problemTranslations]);

  const handleEditorStateChange = (tIndex, newEditorState) => {
    setEditorStates((prevStates) =>
      prevStates.map((state, index) =>
        index === tIndex ? newEditorState : state
      )
    );
    const contentState = newEditorState.getCurrentContent();
    const htmlContent = stateToHTML(contentState);
    handleTranslationChange(tIndex, "descriptionTranslation", htmlContent);
  };

  const handleItemClick = (tIndex) => {
    setExpandedIndex((prev) => (prev === tIndex ? null : tIndex));
  };

  // Handle drag-and-drop sort end
  const onSortEnd = ({ oldIndex, newIndex }) => {
    // Reorder translations
    const reorderedTranslations = arrayMove(
      problemTranslations,
      oldIndex,
      newIndex
    );
    // Reorder editor states to match
    const reorderedEditorStates = arrayMove(editorStates, oldIndex, newIndex);

    // Update parent state with reordered translations
    handleTranslationChange(-1, "reorder", reorderedTranslations);

    // Update local editor states
    setEditorStates(reorderedEditorStates);
  };

  return (
    <AccordionStyled expanded={expanded} onChange={onChange}>
      <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ alignSelf: "center" }}>
          Translations
        </Typography>
        {expanded && (
          <IconButton
            sx={{ color: "#475259" }}
            onClick={(event) => {
              event.stopPropagation();
              handleAddTranslation(event);
            }}
          >
            <AddIcon />
          </IconButton>
        )}
      </AccordionSummaryStyled>
      <SortableTranslationsList
        problemTranslations={problemTranslations}
        expandedIndex={expandedIndex}
        handleItemClick={handleItemClick}
        handleDeleteTranslation={handleDeleteTranslation}
        handleTranslationChange={handleTranslationChange}
        editorStates={editorStates}
        handleEditorStateChange={handleEditorStateChange}
        languages={languages}
        handleAddAlgorithmTranslation={handleAddAlgorithmTranslation}
        handleAlgorithmTranslationChange={handleAlgorithmTranslationChange}
        handleDeleteAlgorithmTranslation={handleDeleteAlgorithmTranslation}
        handleAddCodeSnippet={handleAddCodeSnippet}
        handleCodeSnippetChange={handleCodeSnippetChange}
        handleDeleteCodeSnippet={handleDeleteCodeSnippet}
        programmingLanguages={programmingLanguages}
        onSortEnd={onSortEnd}
        useDragHandle={true}
        axis="y" // Ensure vertical dragging
        lockAxis="y" // Lock to vertical axis
      />
    </AccordionStyled>
  );
};

export default TranslationsAccordion;
